import React, { FC, useState } from 'react';
import { reduxForm, formValueSelector, change } from 'redux-form';
import { connect, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Stack, Text, TextAndButtonGroup } from 'shared-components';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { OwnerFields } from '@components/Apply/OwnersMoreDetailsAboutYou/OwnerFields';
import { getNumberOfOwners } from '@components/Apply/OwnersMoreDetailsAboutYou/getNumberOfOwners';
import { ApplyWizardSteps, STEP_BUSINESS_OWNERS } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import {
  LoanWizardShell,
  LoanSubmitButton,
  getLoanPhaseIndex,
  getLoanFaqForRoute,
} from '@components/Apply/WizardShell';

type FurtherBusinessDetailsProps = {
  loanType: string;
  handleSubmit: () => void;
  invalid: boolean;
  ownersCountStart?: number;
  saving?: boolean;
};

const OwnersMoreDetailsAboutYouWrapper2: FC<FurtherBusinessDetailsProps> = ({
  handleSubmit,
  invalid,
  ownersCountStart,
  saving: savingOnSubmit,
}) => {
  const { saving, currentPath } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);
  const faq = getLoanFaqForRoute(currentPath);
  const form = useForm();
  const [ownersCount, setOwnersCount] = useState<number>(ownersCountStart ?? 1);
  const dispatch = useDispatch();
  const isSubmitting = !!(saving || savingOnSubmit);

  return (
    <LoanWizardShell currentStepIndex={phase} faq={faq}>
      <Helmet>
        <title>More Details About You</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Owner Information
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Owner Information
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <Stack gapPx="50px" justifyItems="center" templateColumns="1fr" fullWidth>
                {Array.from({ length: ownersCount }, (_, index) => index + 1).map((ownerNr) => (
                  <div key={ownerNr}>
                    {ownerNr > 1 && (
                      <Stack
                        className="mb-[10px]"
                        flow="column"
                        gapPx="50px"
                        justifyItems="center"
                        templateColumns="1fr"
                        justifyContent="space-between"
                        fullWidth
                      >
                        <div className="border-charcoal w-full">
                          <Text variant="subheadline-sm">Owner #{ownerNr}</Text>
                          <span></span>
                        </div>
                        {ownerNr === ownersCount && (
                          <TextAndButtonGroup
                            buttonContent="-"
                            text="Remove"
                            buttonProps={{
                              type: 'button',
                            }}
                            onClick={() => {
                              dispatch(change('application', `owner_${ownersCount}_id`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_title`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_ownership`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_ssn_masked`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_dob`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_dob_day`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_dob_month`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_dob_year`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_ssn`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_street`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_city`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_zip`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_suite`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_state`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_first`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_last`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_mobile`, undefined));
                              dispatch(change('application', `owner_${ownersCount}_email`, undefined));
                              setOwnersCount(ownersCount - 1);
                            }}
                          />
                        )}
                      </Stack>
                    )}
                    <OwnerFields withAllFields={ownerNr > 1} ownerNr={ownerNr} />
                  </div>
                ))}
              </Stack>

              <div className="w-full flex justify-center pt-[10px]">
                <TextAndButtonGroup
                  buttonContent="+"
                  text="Add additional owners?"
                  onClick={() => setOwnersCount(ownersCount + 1)}
                  buttonProps={{
                    type: 'button',
                  }}
                  stackProps={{
                    className: 'max-w-[368px]',
                  }}
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton invalid={invalid} saving={isSubmitting} data-testid="info_continue">
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

const OwnersMoreDetailsAboutYouWrapper1 = reduxForm<FurtherBusinessDetailsProps, FurtherBusinessDetailsProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_OWNERS].zodSchema),
})(OwnersMoreDetailsAboutYouWrapper2);

const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    ownersCountStart: getNumberOfOwners(state),
  };
};

export const OwnersMoreDetailsAboutYou = withOnSubmit(
  connect(mapStateToProps)(OwnersMoreDetailsAboutYouWrapper1),
  STEP_BUSINESS_OWNERS
);
